import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { themes, defaultThemeId, getThemeById, getAvailableThemesList } from '../theme';
import { updateThemeForSite } from '../services/CommonServices';

const ThemeContext = createContext();

const THEME_STORAGE_KEY = 'bubanc-theme';
const CUSTOM_COLORS_STORAGE_KEY = 'bubanc-custom-colors';
const LAYOUT_MODE_STORAGE_KEY = 'bubanc-layout-mode';
const FONT_SETTINGS_STORAGE_KEY = 'bubanc-font-settings';

/**
 * Layout Modes
 * bordered: 테두리/그림자가 있는 카드 스타일 레이아웃
 * clean: 테두리 없이 너비 제한만 있는 클린 레이아웃
 */
export const LAYOUT_MODES = {
  bordered: {
    id: 'bordered',
    name: 'Bordered',
    description: '테두리와 그림자가 있는 카드 스타일',
  },
  clean: {
    id: 'clean',
    name: 'Clean',
    description: '테두리 없이 너비 제한만 있는 스타일',
  },
};

const defaultLayoutMode = 'bordered';

/**
 * 색상 카테고리 정의 (UI 표시용)
 */
export const COLOR_CATEGORIES = {
  primary: {
    label: 'Primary Colors',
    labelKo: '주요 색상',
    colors: ['primary', 'primaryHover', 'secondary', 'secondaryHover'],
  },
  background: {
    label: 'Background',
    labelKo: '배경 색상',
    colors: ['background', 'backgroundAlt', 'backgroundDark'],
  },
  text: {
    label: 'Text',
    labelKo: '텍스트 색상',
    colors: ['text', 'textLight', 'textMuted', 'textSecondary'],
  },
  ui: {
    label: 'UI Elements',
    labelKo: 'UI 요소',
    colors: ['border', 'borderLight'],
  },
  headerFooter: {
    label: 'Header & Footer',
    labelKo: '헤더 & 푸터',
    colors: ['headerBg', 'headerText', 'footerBg', 'footerText'],
  },
  status: {
    label: 'Status Colors',
    labelKo: '상태 색상',
    colors: ['success', 'warning', 'danger', 'info'],
  },
};

/**
 * 색상 라벨 (한글)
 */
export const COLOR_LABELS = {
  primary: '주요 색상',
  primaryHover: '주요 색상 (Hover)',
  secondary: '보조 색상',
  secondaryHover: '보조 색상 (Hover)',
  background: '배경',
  backgroundAlt: '배경 (대체)',
  backgroundDark: '배경 (어두운)',
  text: '텍스트',
  textLight: '텍스트 (밝은)',
  textMuted: '텍스트 (흐린)',
  textSecondary: '텍스트 (보조)',
  border: '테두리',
  borderLight: '테두리 (밝은)',
  shadow: '그림자',
  shadowDark: '그림자 (어두운)',
  success: '성공',
  warning: '경고',
  danger: '위험',
  info: '정보',
  headerBg: '헤더 배경',
  headerText: '헤더 텍스트',
  footerBg: '푸터 배경',
  footerText: '푸터 텍스트',
};

/**
 * Google Fonts - 자주 사용되는 폰트 목록
 */
export const GOOGLE_FONTS = [
  { name: 'Noto Sans KR', value: 'Noto Sans KR', category: 'sans-serif', korean: true },
  { name: 'Pretendard', value: 'Pretendard', category: 'sans-serif', korean: true },
  { name: 'Nanum Gothic', value: 'Nanum Gothic', category: 'sans-serif', korean: true },
  { name: 'Nanum Myeongjo', value: 'Nanum Myeongjo', category: 'serif', korean: true },
  { name: 'Gothic A1', value: 'Gothic A1', category: 'sans-serif', korean: true },
  { name: 'Roboto', value: 'Roboto', category: 'sans-serif', korean: false },
  { name: 'Open Sans', value: 'Open Sans', category: 'sans-serif', korean: false },
  { name: 'Lato', value: 'Lato', category: 'sans-serif', korean: false },
  { name: 'Montserrat', value: 'Montserrat', category: 'sans-serif', korean: false },
  { name: 'Poppins', value: 'Poppins', category: 'sans-serif', korean: false },
  { name: 'Playfair Display', value: 'Playfair Display', category: 'serif', korean: false },
  { name: 'Merriweather', value: 'Merriweather', category: 'serif', korean: false },
  { name: 'Inter', value: 'Inter', category: 'sans-serif', korean: false },
  { name: 'Source Sans Pro', value: 'Source Sans Pro', category: 'sans-serif', korean: false },
];

/**
 * 폰트 설정 카테고리
 */
export const FONT_CATEGORIES = {
  headings: {
    label: 'Headings',
    labelKo: '제목',
    description: 'H1, H2, H3 등 제목 텍스트',
    group: 'global',
  },
  body: {
    label: 'Body',
    labelKo: '본문',
    description: '일반 본문 텍스트',
    group: 'global',
  },
  heroEnglish: {
    label: 'Hero English',
    labelKo: 'Hero 영문',
    description: 'Hero 섹션 영문 텍스트 (제목)',
    group: 'hero',
  },
  heroKorean: {
    label: 'Hero Korean',
    labelKo: 'Hero 한글',
    description: 'Hero 섹션 한글 텍스트 (본문)',
    group: 'hero',
  },
};

/**
 * 기본 폰트 설정
 */
export const DEFAULT_FONT_SETTINGS = {
  headings: {
    fontFamily: 'Noto Sans KR',
    fontSize: '1',      // 배율 (1 = 100%)
    fontWeight: '700',
    letterSpacing: '0', // em 단위
    lineHeight: '1.3',
  },
  body: {
    fontFamily: 'Noto Sans KR',
    fontSize: '1',
    fontWeight: '400',
    letterSpacing: '0',
    lineHeight: '1.6',
  },
  heroEnglish: {
    fontFamily: 'Playfair Display',
    fontSize: '1',
    fontWeight: '700',
    letterSpacing: '0',
    lineHeight: '1.2',
  },
  heroKorean: {
    fontFamily: 'Noto Sans KR',
    fontSize: '1',
    fontWeight: '400',
    letterSpacing: '0',
    lineHeight: '1.6',
  },
};

/**
 * useTheme Hook
 * 테마 컨텍스트에 접근하기 위한 훅
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * ThemeProvider Component
 * 테마 상태 관리 및 스위칭 기능 제공
 */
export const ThemeProvider = ({ children, siteId, initialThemeId }) => {
  // 서버에서 전달받은 테마 ID 우선, 없으면 localStorage, 최종적으로 기본값
  const getInitialTheme = () => {
    // 1. 서버에서 전달받은 테마 ID가 있으면 우선 사용 (모든 기기에서 동일)
    if (initialThemeId && themes[initialThemeId]) {
      return initialThemeId;
    }
    // 2. localStorage fallback (로컬 개발 등)
    try {
      const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
      if (savedTheme && themes[savedTheme]) {
        return savedTheme;
      }
    } catch (e) {
      console.warn('Failed to load theme from localStorage:', e);
    }
    return defaultThemeId;
  };

  // localStorage에서 저장된 커스텀 색상 불러오기
  const getInitialCustomColors = () => {
    try {
      const savedColors = localStorage.getItem(CUSTOM_COLORS_STORAGE_KEY);
      if (savedColors) {
        return JSON.parse(savedColors);
      }
    } catch (e) {
      console.warn('Failed to load custom colors from localStorage:', e);
    }
    return {};
  };

  // localStorage에서 저장된 레이아웃 모드 불러오기
  const getInitialLayoutMode = () => {
    try {
      const savedMode = localStorage.getItem(LAYOUT_MODE_STORAGE_KEY);
      if (savedMode && LAYOUT_MODES[savedMode]) {
        return savedMode;
      }
    } catch (e) {
      console.warn('Failed to load layout mode from localStorage:', e);
    }
    return defaultLayoutMode;
  };

  // localStorage에서 저장된 폰트 설정 불러오기
  const getInitialFontSettings = () => {
    try {
      const savedFonts = localStorage.getItem(FONT_SETTINGS_STORAGE_KEY);
      if (savedFonts) {
        return { ...DEFAULT_FONT_SETTINGS, ...JSON.parse(savedFonts) };
      }
    } catch (e) {
      console.warn('Failed to load font settings from localStorage:', e);
    }
    return DEFAULT_FONT_SETTINGS;
  };

  const [currentThemeId, setCurrentThemeId] = useState(getInitialTheme);
  const [customColors, setCustomColors] = useState(getInitialCustomColors);
  const [layoutMode, setLayoutModeState] = useState(getInitialLayoutMode);
  const [fontSettings, setFontSettingsState] = useState(getInitialFontSettings);
  const [isLoading, setIsLoading] = useState(false);

  // 기본 테마 객체
  const baseTheme = getThemeById(currentThemeId);

  // 커스텀 색상이 적용된 최종 테마
  const currentTheme = {
    ...baseTheme,
    colors: {
      ...baseTheme.colors,
      ...customColors,
    },
  };

  // localStorage 저장 - 테마 ID
  const saveThemeToLocalStorage = useCallback((themeId) => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, themeId);
    } catch (e) {
      console.warn('Failed to save theme to localStorage:', e);
    }
  }, []);

  // localStorage 저장 - 커스텀 색상
  const saveCustomColorsToLocalStorage = useCallback((colors) => {
    try {
      localStorage.setItem(CUSTOM_COLORS_STORAGE_KEY, JSON.stringify(colors));
    } catch (e) {
      console.warn('Failed to save custom colors to localStorage:', e);
    }
  }, []);

  // 백엔드 API 동기화
  const syncWithBackend = useCallback(async (themeId) => {
    if (!siteId) return;

    try {
      await updateThemeForSite(siteId, themeId);
    } catch (e) {
      console.warn('Failed to sync theme with backend:', e);
    }
  }, [siteId]);

  // 테마 변경 함수
  const setTheme = useCallback(async (themeId) => {
    if (!themes[themeId]) {
      console.warn(`Theme "${themeId}" not found. Using default theme.`);
      themeId = defaultThemeId;
    }

    setIsLoading(true);

    try {
      // 상태 업데이트
      setCurrentThemeId(themeId);

      // 테마 변경 시 커스텀 색상 초기화 (선택적)
      // setCustomColors({});
      // saveCustomColorsToLocalStorage({});

      // localStorage 저장
      saveThemeToLocalStorage(themeId);

      // 백엔드 동기화 (비동기, 실패해도 로컬은 유지)
      await syncWithBackend(themeId);
    } finally {
      setIsLoading(false);
    }
  }, [saveThemeToLocalStorage, syncWithBackend]);

  // 개별 색상 변경 함수
  const setColor = useCallback((colorKey, value) => {
    setCustomColors((prev) => {
      const newColors = { ...prev, [colorKey]: value };
      saveCustomColorsToLocalStorage(newColors);
      return newColors;
    });
  }, [saveCustomColorsToLocalStorage]);

  // 여러 색상 한번에 변경
  const setColors = useCallback((colors) => {
    setCustomColors((prev) => {
      const newColors = { ...prev, ...colors };
      saveCustomColorsToLocalStorage(newColors);
      return newColors;
    });
  }, [saveCustomColorsToLocalStorage]);

  // 커스텀 색상 초기화 (기본 테마로 복원)
  const resetColors = useCallback(() => {
    setCustomColors({});
    saveCustomColorsToLocalStorage({});
  }, [saveCustomColorsToLocalStorage]);

  // 현재 테마의 기본 색상으로 커스텀 색상 초기화
  const resetToThemeDefaults = useCallback(() => {
    setCustomColors({});
    saveCustomColorsToLocalStorage({});
  }, [saveCustomColorsToLocalStorage]);

  // 테마 토글 (2개 테마 간 전환)
  const toggleTheme = useCallback(() => {
    const themeIds = Object.keys(themes);
    const currentIndex = themeIds.indexOf(currentThemeId);
    const nextIndex = (currentIndex + 1) % themeIds.length;
    setTheme(themeIds[nextIndex]);
  }, [currentThemeId, setTheme]);

  // 레이아웃 모드 변경 함수
  const setLayoutMode = useCallback((mode) => {
    if (!LAYOUT_MODES[mode]) {
      console.warn(`Layout mode "${mode}" not found. Using default.`);
      mode = defaultLayoutMode;
    }

    setLayoutModeState(mode);

    // localStorage 저장
    try {
      localStorage.setItem(LAYOUT_MODE_STORAGE_KEY, mode);
    } catch (e) {
      console.warn('Failed to save layout mode to localStorage:', e);
    }
  }, []);

  // 레이아웃 모드 토글
  const toggleLayoutMode = useCallback(() => {
    const newMode = layoutMode === 'bordered' ? 'clean' : 'bordered';
    setLayoutMode(newMode);
  }, [layoutMode, setLayoutMode]);

  // 폰트 설정 저장
  const saveFontSettingsToLocalStorage = useCallback((settings) => {
    try {
      localStorage.setItem(FONT_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
      console.warn('Failed to save font settings to localStorage:', e);
    }
  }, []);

  // 폰트 설정 변경 (카테고리별)
  const setFontSettings = useCallback((category, settings) => {
    setFontSettingsState((prev) => {
      const newSettings = {
        ...prev,
        [category]: { ...prev[category], ...settings },
      };
      saveFontSettingsToLocalStorage(newSettings);
      return newSettings;
    });
  }, [saveFontSettingsToLocalStorage]);

  // 전체 폰트 설정 변경
  const setAllFontSettings = useCallback((settings) => {
    setFontSettingsState(settings);
    saveFontSettingsToLocalStorage(settings);
  }, [saveFontSettingsToLocalStorage]);

  // 폰트 설정 초기화
  const resetFontSettings = useCallback(() => {
    setFontSettingsState(DEFAULT_FONT_SETTINGS);
    saveFontSettingsToLocalStorage(DEFAULT_FONT_SETTINGS);
  }, [saveFontSettingsToLocalStorage]);

  // CSS 변수 적용 (테마 + 레이아웃 모드 + 폰트)
  useEffect(() => {
    const root = document.documentElement;
    const colors = currentTheme.colors;

    // 색상 변수 적용
    Object.entries(colors).forEach(([key, value]) => {
      // camelCase to kebab-case
      const cssVarName = `--color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
      root.style.setProperty(cssVarName, value);
    });

    // 폰트 설정 CSS 변수 적용
    Object.entries(fontSettings).forEach(([category, settings]) => {
      Object.entries(settings).forEach(([prop, value]) => {
        // camelCase to kebab-case for property name
        const propKebab = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
        const cssVarName = `--font-${category}-${propKebab}`;
        root.style.setProperty(cssVarName, value);
      });
    });

    // 레이아웃 모드에 따른 클래스 적용
    root.setAttribute('data-layout-mode', layoutMode);
    root.setAttribute('data-theme', currentThemeId);
  }, [currentTheme, currentThemeId, layoutMode, fontSettings]);

  const value = {
    // 현재 테마 정보
    currentThemeId,
    currentTheme,
    baseTheme,
    themeName: currentTheme.name,
    colors: currentTheme.colors,

    // 커스텀 색상
    customColors,
    setColor,
    setColors,
    resetColors,
    resetToThemeDefaults,
    hasCustomColors: Object.keys(customColors).length > 0,

    // 테마 변경 함수
    setTheme,
    toggleTheme,

    // 사용 가능한 테마 목록
    availableThemes: getAvailableThemesList(),

    // 레이아웃 모드
    layoutMode,
    setLayoutMode,
    toggleLayoutMode,
    availableLayoutModes: Object.values(LAYOUT_MODES),

    // 폰트 설정
    fontSettings,
    setFontSettings,
    setAllFontSettings,
    resetFontSettings,
    fontCategories: FONT_CATEGORIES,
    availableFonts: GOOGLE_FONTS,
    defaultFontSettings: DEFAULT_FONT_SETTINGS,

    // 색상 카테고리 및 라벨 (UI용)
    colorCategories: COLOR_CATEGORIES,
    colorLabels: COLOR_LABELS,

    // 상태
    isLoading,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
